
import { useState, useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Gerador } from "@/types";
import { UseFormReturn } from "react-hook-form";
import { ClienteFormValues } from "./types";
import { Progress } from "@/components/ui/progress";
import { Sun, AlertTriangle, Check, Zap } from "lucide-react";

interface GeradorSelectorProps {
  form: UseFormReturn<ClienteFormValues>;
  geradores: Gerador[];
  geradorSelecionado: Gerador | null;
  setGeradorSelecionado: (gerador: Gerador | null) => void;
  usarDadosGerador: boolean;
  setUsarDadosGerador: (usar: boolean) => void;
}

const GeradorSelector = ({
  form,
  geradores,
  geradorSelecionado,
  setGeradorSelecionado,
  usarDadosGerador,
  setUsarDadosGerador
}: GeradorSelectorProps) => {
  const [geradorFiltrado, setGeradorFiltrado] = useState<Gerador[]>(geradores);
  const [filtro, setFiltro] = useState<string>("todos");
  
  useEffect(() => {
    if (filtro === "disponivel") {
      setGeradorFiltrado(geradores.filter(g => g.creditosDisponiveis > 0));
    } else {
      setGeradorFiltrado(geradores);
    }
  }, [filtro, geradores]);
  
  const onGeradorChange = (geradorId: string) => {
    const gerador = geradores.find(g => g.id === geradorId);
    setGeradorSelecionado(gerador || null);
    
    if (gerador && usarDadosGerador) {
      form.setValue("concessionaria", gerador.concessionaria);
    }
  };
  
  const onToggleUsarDadosGerador = (checked: boolean) => {
    setUsarDadosGerador(checked);
    
    if (checked && geradorSelecionado) {
      form.setValue("concessionaria", geradorSelecionado.concessionaria);
    }
  };

  const consumoEstimado = form.watch("gastoEnergia") / form.watch("tarifaEnergia") || 0;
  const percentualCapacidade = geradorSelecionado && geradorSelecionado.geracaoEstimada > 0
    ? (consumoEstimado / geradorSelecionado.geracaoEstimada) * 100
    : 0;
  
  const temCreditoSuficiente = geradorSelecionado 
    ? geradorSelecionado.creditosDisponiveis >= consumoEstimado
    : true;

  return (
    <div className="bg-teal-50 p-4 rounded-md space-y-4">
      <h3 className="text-sm font-medium text-teal-800 mb-3 flex items-center">
        <Sun className="h-4 w-4 mr-2" />
        Selecione o Gerador de Energia
      </h3>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <button
          type="button"
          onClick={() => setFiltro("todos")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            filtro === "todos" 
              ? "bg-teal-600 text-white" 
              : "bg-white text-teal-700 border border-teal-200 hover:bg-teal-50"
          }`}
        >
          Todos os Geradores
        </button>
        <button
          type="button"
          onClick={() => setFiltro("disponivel")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            filtro === "disponivel" 
              ? "bg-teal-600 text-white" 
              : "bg-white text-teal-700 border border-teal-200 hover:bg-teal-50"
          }`}
        >
          Apenas com Créditos
        </button>
      </div>
      
      <FormField
        control={form.control}
        name="geradorId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Gerador de Energia</FormLabel>
            <Select 
              onValueChange={(value) => {
                field.onChange(value);
                onGeradorChange(value);
              }}
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger className="border-teal-200 focus:border-teal-500">
                  <SelectValue placeholder="Selecione o gerador" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {geradorFiltrado.length === 0 ? (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    Nenhum gerador disponível
                  </div>
                ) : (
                  geradorFiltrado.map((gerador) => (
                    <SelectItem 
                      key={gerador.id} 
                      value={gerador.id}
                    >
                      <div className="flex flex-col">
                        <span>{gerador.nomeTitular} - {gerador.cidade}/{gerador.estado}</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          {gerador.creditosDisponiveis.toFixed(0)} kWh disponíveis
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {geradorSelecionado && (
        <div className="bg-white rounded-md shadow-sm p-4 mt-4 border border-teal-100">
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox 
              id="use-gerador-data" 
              checked={usarDadosGerador}
              onCheckedChange={onToggleUsarDadosGerador}
              className="text-teal-600 border-teal-300"
            />
            <label 
              htmlFor="use-gerador-data"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Usar a mesma concessionária do gerador
            </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-teal-50 p-3 rounded-md">
              <p className="text-xs text-muted-foreground">Concessionária</p>
              <p className="font-medium text-teal-900">{geradorSelecionado.concessionaria}</p>
            </div>
            <div className="bg-teal-50 p-3 rounded-md">
              <p className="text-xs text-muted-foreground">Tarifa de Venda</p>
              <p className="font-medium text-teal-900">R$ {geradorSelecionado.tarifaVenda.toFixed(4)}/kWh</p>
            </div>
            <div className="bg-teal-50 p-3 rounded-md">
              <p className="text-xs text-muted-foreground">Créditos Disponíveis</p>
              <p className="font-medium text-teal-900">
                {geradorSelecionado.creditosDisponiveis.toFixed(2)} kWh/mês
              </p>
            </div>
          </div>
          
          {consumoEstimado > 0 && (
            <div className="space-y-3 mt-4">
              <div className="flex justify-between text-sm">
                <span>Consumo estimado: <strong>{consumoEstimado.toFixed(2)} kWh/mês</strong></span>
                <span>Capacidade do gerador: <strong>{geradorSelecionado.geracaoEstimada.toFixed(2)} kWh/mês</strong></span>
              </div>
              
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Percentual da capacidade</span>
                  <span className={percentualCapacidade > 100 ? 'text-red-600' : 'text-teal-600'}>
                    {percentualCapacidade.toFixed(1)}%
                  </span>
                </div>
                <Progress 
                  value={Math.min(percentualCapacidade, 100)} 
                  className="h-2" 
                  indicatorClassName={percentualCapacidade > 100 ? 'bg-red-500' : 'bg-teal-500'} 
                />
              </div>
              
              {!temCreditoSuficiente && (
                <div className="flex items-center gap-2 text-sm text-amber-600 mt-2 p-2 bg-amber-50 rounded-md">
                  <AlertTriangle className="h-4 w-4" />
                  <span>
                    Atenção: O consumo estimado ({consumoEstimado.toFixed(2)} kWh) é maior que os créditos disponíveis ({geradorSelecionado.creditosDisponiveis.toFixed(2)} kWh)
                  </span>
                </div>
              )}
              
              {temCreditoSuficiente && (
                <div className="flex items-center gap-2 text-sm text-green-600 mt-2 p-2 bg-green-50 rounded-md">
                  <Check className="h-4 w-4" />
                  <span>
                    O gerador possui créditos suficientes para suprir o consumo do cliente.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeradorSelector;
